import { Component, ElementRef, HostListener } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexPlotOptions,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ChartSelectionTypeModel,
  DashboardStatisticsModel,
} from '../../../../core/models/dashboard/dashboard-statistics.model';
import { DashboardHeaderItemsModel } from '../../../../core/models/dashboard/dashboard-headeritems.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { translateChartLabels } from '../../../../utils/chart-labels-utils';
import { UserRole } from '../../../../../enums/roles.enum';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { NgApexchartsModule } from 'ng-apexcharts';
import { AppointmentsService } from '../../../../core/services/appointment/appointments.service';
export type ApexOptions = {
  series: ApexAxisChartSeries;
  colors: string[];
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  markers: ApexMarkers;
  yaxis: ApexYAxis | ApexYAxis[];
  plotOptions: ApexPlotOptions;
};
@Component({
  selector: 'app-dashboar-main',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, NgApexchartsModule],
  templateUrl: './dashboard-main.component.html',
  styleUrl: './dashboard-main.component.scss',
})
export class DashboarMainComponent {
  apxOptions: Partial<ApexOptions> | null = null;

  dashboardStatistics: DashboardStatisticsModel = {
    totalPatientCount: 0,
    totalImageCount: 0,
    totalConsultationCount: 0,
    totalAppointmentsCount: 0,
    totalFollowUpPatientCountByTwoWeeks: 0,
    totalFollowUpPatientCountByOneMonth: 0,
    totalFollowUpPatientCountByTwoMonths: 0,
    totalPatientIncreasePercentageLastMonth: 0,
    totalImageIncreasePercentageLastMonth: 0,
    totalConsultationIncreasePercentageLastMonth: 0,
    totalIncomeIncreasePercentageLastMonth: 0,
    statistics: [],
  };

  dashboardHeaderItems: DashboardHeaderItemsModel[] = [
    {
      countKey: 'totalPatientCount',
      icon: '/assets/media/dashboard/totalPatient.svg',
      label: 'totalPatients',
      increasePercentageLastMonth: 0,
    },
    {
      countKey: 'totalImageCount',
      icon: '/assets/media/dashboard/totalScan.svg',
      label: 'totalScans',
      increasePercentageLastMonth: 0,
    },
    {
      countKey: 'totalConsultationCount',
      icon: '/assets/media/dashboard/totalConsult.svg',
      label: 'totalConsultations',
      increasePercentageLastMonth: 0,
    },
    {
      countKey: 'totalAppointmentsCount',
      icon: '/assets/media/dashboard/totalAppointment.svg',
      label: 'totalAppointments',
      increasePercentageLastMonth: 0,
    },
  ];

  chartSelectionType: ChartSelectionTypeModel[] = [
    {
      value: 1,
      label: 'dashboard.selectbox.weekly',
    },
    {
      value: 2,
      label: 'dashboard.selectbox.monthly',
    },
    {
      value: 3,
      label: 'dashboard.selectbox.yearly',
    },
  ];

  // Chart data dizileri
  totalChartPatients: number[] = [];
  totalChartScans: number[] = [];
  totalChartFinishedWounds: number[] = [];

  // Eski alanlar kullanılmıyor ama bırakalım istersen
  totalPatientCount: 0;
  totalImageCount: 0;
  totalECGCount: 0;
  totalConsultationCount: 0;
  totalChartConsultations: number[] = [];
  totalChartIncome: number[] = [];

  chartLabels: string[] = [];
  selectedChartType: number = 1;
  private initialCanvasHeight: number | undefined;
  private initialCanvasWidth: number | undefined;

  daysOfWeek: string[] = []; // x-axis label (weekly & monthly)
  years: string[] = []; // x-axis label (yearly: ay isimleri vs.)

  currentUser: UserSessionModel | null = null;
  userRole: any = UserRole;

  constructor(
    private patientsService: PatientsService,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private currentUserService: CurrentUserService,
    private appointmentsService: AppointmentsService,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.adjustCanvasHeight();
  }

  ngAfterViewInit(): void {
    this.adjustCanvasHeight();
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.getDashboardInformations();
    this.getAppointmentCount();
  }

  private adjustCanvasHeight(): void {
    const statisticsSection =
      this.elementRef.nativeElement.querySelector('.col-md-4');
    const canvas = this.elementRef.nativeElement.querySelector('#lineChart');
    if (statisticsSection && canvas) {
      const statisticsHeight = statisticsSection.offsetHeight;
      canvas.style.height = `${statisticsHeight}px`;
    }
  }

  private buildApexOptions(type: number): void {
    const categories =
      type === 1 || type === 2
        ? this.daysOfWeek
        : type === 3
          ? this.years
          : this.chartLabels;

    this.apxOptions = {
      series: [
        {
          name: this.translate.instant('dashboard.patients'),
          type: 'line',
          data: this.totalChartPatients,
        },
        {
          name: this.translate.instant('dashboard.scans'),
          type: 'line',
          data: this.totalChartScans,
        },
        {
          name: this.translate.instant('dashboard.finishedWounds'),
          type: 'line',
          data: this.totalChartFinishedWounds,
        },
      ],
      chart: {
        type: 'line',
        foreColor: 'var(--text-secondary)',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [
        'var(--brand-primary)',
        'var(--brand-primary-active)',
        'rgba(var(--brand-primary-rgb), 0.6)',
      ],
      xaxis: {
        categories,
      },
      stroke: {
        curve: 'smooth',
        width: [3, 3, 0], // bar için 0
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        strokeDashArray: 1,
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
      legend: {
        show: true,
      },
      markers: {
        size: 4,
      },
      yaxis: {
        decimalsInFloat: 0,
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 4,
        },
      },
    };
  }

  getDashboardInformations(): void {
    this.patientsService.dashboardStatistics(this.selectedChartType).subscribe({
      next: (data) => {
        if (!data) {
          return;
        }

        const stats = data.statistics || [];

        this.totalChartPatients = stats.map((item: any) => item.totalPatients);
        this.totalChartScans = stats.map((item: any) => item.totalScans);
        this.totalChartFinishedWounds = stats.map(
          (item: any) => item.totalFinishedWounds,
        );

        this.daysOfWeek = stats.map((item: any) =>
          translateChartLabels(this.translate, item.dayOfWeek, 'day'),
        );

        this.years = stats.map((item: any) =>
          translateChartLabels(this.translate, item.month, 'year'),
        );

        this.dashboardStatistics.totalPatientCount = data.totalPatientsCount;
        this.dashboardStatistics.totalImageCount = data.totalScans;
        this.dashboardStatistics.totalConsultationCount =
          data.totalFinishedWounds;

        this.dashboardStatistics.totalFollowUpPatientCountByTwoWeeks =
          data.totalFollowUpPatientCountByTwoWeeks;
        this.dashboardStatistics.totalFollowUpPatientCountByOneMonth =
          data.totalFollowUpPatientCountByOneMonth;
        this.dashboardStatistics.totalFollowUpPatientCountByTwoMonths =
          data.totalFollowUpPatientCountByTwoMonths;

        this.dashboardStatistics.statistics = stats;

        this.buildApexOptions(this.selectedChartType);
      },
    });
  }

  onChangeChart(e: Event): void {
    const value = Number((e.target as HTMLSelectElement).value);
    this.selectedChartType = value;
    this.getDashboardInformations();
    this.getAppointmentCount();
  }

  getArrowStyle(percentage: number): string {
    if (percentage > 0) {
      return 'text-success fa-arrow-up rotate-45';
    } else if (percentage < 0) {
      return 'text-danger fa-arrow-down rotate-45';
    } else {
      return 'fa-minus';
    }
  }

  getAppointmentCount(): void {
    this.appointmentsService.getAppointmentCount().subscribe({
      next: (res) => {
        if (res && res.data) {
          this.dashboardStatistics.totalAppointmentsCount = res.data;
        }
      },
      error: (err) => console.error(err),
    });
  }
}
